import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import Loader from "../../common/Loader";
import { getAdsTable, AdTableRow } from "../../../services/meta/metaService";
import { getProducts } from "../../../services/products/productAxios";
import { Product } from "../../products/ProductInterface";
import ProductsDashboard from "./ProductsDashboard";
import ProductHeader from "./ProductHeader";
import ProductCreativesView from "./ProductCreativesView";
import AssignmentInboxModal from "./AssignmentInboxModal";
import AdDetailModal from "../AdDetailModal";

interface PerformanceCreativosTabProps {
  selectedAdAccount: string | null;
  dateStart: string;
  dateEnd: string;
  refreshKey?: number;
}

export const PerformanceCreativosTab = ({
  selectedAdAccount,
  dateStart,
  dateEnd,
  refreshKey,
}: PerformanceCreativosTabProps) => {
  const { language } = useAuth();

  const [products, setProducts] = useState<Product[]>([]);
  const [ads, setAds] = useState<AdTableRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [detailAd, setDetailAd] = useState<AdTableRow | null>(null);

  useEffect(() => {
    if (!selectedAdAccount) return;
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      getProducts(),
      getAdsTable(selectedAdAccount, dateStart, dateEnd),
    ])
      .then(([productsResult, adsResult]) => {
        if (cancelled) return;
        setProducts(productsResult.status === "fulfilled" ? (productsResult.value ?? []) : []);
        setAds(adsResult.status === "fulfilled" ? (adsResult.value.ads ?? []) : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAdAccount, dateStart, dateEnd, refreshKey]);

  const selectedProduct = useMemo(
    () => (selectedProductId ? products.find((p) => p.id === selectedProductId) ?? null : null),
    [products, selectedProductId],
  );

  const productAds = useMemo(
    () => (selectedProductId ? ads.filter((a) => a.product_id === selectedProductId) : []),
    [ads, selectedProductId],
  );

  const unassignedAds = useMemo(
    () => ads.filter((a) => !a.product_id),
    [ads],
  );

  const handleAssigned = (adId: string, productId: string) => {
    const product = products.find((p) => p.id === productId);
    setAds((prev) =>
      prev.map((a) =>
        a.ad_id === adId
          ? { ...a, product_id: productId, product_name: product?.name ?? null }
          : a,
      ),
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader />
      </div>
    );
  }

  if (selectedProduct) {
    return (
      <div className="bg-gray-50 min-h-[60vh]">
        <ProductHeader
          product={selectedProduct}
          productAds={productAds}
          language={language}
          onBack={() => setSelectedProductId(null)}
        />
        <ProductCreativesView
          ads={productAds}
          language={language}
          onAdClick={setDetailAd}
        />

        {detailAd && selectedAdAccount && (
          <AdDetailModal
            ad={detailAd}
            adAccountId={selectedAdAccount}
            dateStart={dateStart}
            dateEnd={dateEnd}
            language={language}
            onClose={() => setDetailAd(null)}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <ProductsDashboard
        products={products}
        ads={ads}
        language={language}
        onSelectProduct={setSelectedProductId}
        onOpenUnassigned={() => setAssignmentOpen(true)}
      />

      {assignmentOpen && (
        <AssignmentInboxModal
          unassignedAds={unassignedAds}
          products={products}
          language={language}
          onClose={() => setAssignmentOpen(false)}
          onAssigned={handleAssigned}
        />
      )}
    </>
  );
};

export default PerformanceCreativosTab;
